import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { LegalDisclaimer } from "./LegalDisclaimer";
import { Stethoscope, LogOut, Settings, KeyRound } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const ROLE_LABEL = { admin: "Amministratore", medico: "Medico", isf: "Informatore Scientifico" };

export function AppLayout({ children, title, onOpenSettings }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwdOpen, setPwdOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = user
    ? `${(user.nome || "")[0] || ""}${(user.cognome || "")[0] || ""}`.toUpperCase()
    : "";

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-700 text-white">
              <Stethoscope className="h-5 w-5" />
            </div>
            <div>
              <p className="font-heading text-sm font-800 leading-tight text-slate-900">
                Agenda-ISF-COMO
              </p>
              <p className="text-[11px] leading-tight text-slate-400">{title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-slate-100"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-xs font-600 text-blue-700">
                    {initials || "U"}
                  </span>
                  <span className="hidden text-sm font-500 text-slate-700 sm:block">
                    {user?.nome} {user?.cognome}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-600 text-slate-900">{user?.nome} {user?.cognome}</div>
                  <div className="text-xs font-400 text-slate-400">{ROLE_LABEL[user?.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {onOpenSettings && (
                  <DropdownMenuItem onClick={onOpenSettings} data-testid="open-settings-item">
                    <Settings className="mr-2 h-4 w-4" /> Preferenze notifiche
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="change-password-item">
                    <KeyRound className="mr-2 h-4 w-4" /> Cambia password
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8">{children}</main>

      <LegalDisclaimer variant="footer" />
      {user?.role === "admin" && <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />}
    </div>
  );
}
